import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar, Footer } from "@/components/shared/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
// `/` SERVES HomeLanding.jsx — the Landing v2 rebuild — as of 2026-08-19.
//
// THE OTHER TWO HOMEPAGES WERE DELETED ON 2026-08-31, unrouted since cutover.
//
// HomePageV2.js was the first attempt, rolled back 2026-08-17 for reproducing
// the design's copy but not its structure; its own note already said it "can be
// deleted". HomePage.js was landing v1, kept unrouted as a one-line rollback.
// Twelve days on, Landing v2 is live and stable, and the two files had become a
// liability rather than insurance: they carried their own $49 and $25 price
// literals, so every repricing had to edit pages nobody could reach, and a
// guard sweeping src/ had to keep budgeting for them.
//
// THE ROLLBACK IS GIT, WHICH IS WHERE IT ALWAYS WAS. Landing v1 is recoverable
// at any commit before this one; the version stripped of its fabricated claims
// is 61f28cf / 19af966 / 16421fa.
import HomeLanding from "@/pages/HomeLanding";
import UxAuditorPage from "@/pages/UxAuditorPage";
import PricingPage from "@/pages/PricingPage";
import ComparePage from "@/pages/ComparePage";
import MvpValidatorPage from "@/pages/MvpValidatorPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import UseCasesPage from "@/pages/UseCasesPage";
import DashboardPage from "@/pages/DashboardPage";
import NewAuditPage from "@/pages/NewAuditPage";
import AuditReportPage from "@/pages/AuditReportPage";
import CompareAuditsPage from "@/pages/CompareAuditsPage";
import AccountPage from "@/pages/AccountPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import LoginPage from "@/pages/LoginPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SecurityPage from "@/pages/SecurityPage";
import LegalPage from "@/pages/LegalPage";
import AboutPage from "@/pages/AboutPage";
import ClaudePage from "@/pages/ClaudePage";
import ContactPage from "@/pages/ContactPage";
import UseCasesSaasPage from "@/pages/UseCasesSaasPage";
import UseCasesAgenciesPage from "@/pages/UseCasesAgenciesPage";
import UseCasesStartupsPage from "@/pages/UseCasesStartupsPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import UxAuditPillarPage from "@/pages/UxAuditPillarPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ThankYouPage from "@/pages/ThankYouPage";
import SampleReportPage from "@/pages/SampleReportPage";
import "@/App.css";

function AppRouter() {
  const location = useLocation();

  // Login page - no navbar/footer
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }

  // Admin routes don't show regular navbar/footer
  if (location.pathname.startsWith('/admin')) {
    return (
      <Routes>
        <Route path="/admin/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      </Routes>
    );
  }

  return (
    <>
      <Navbar />
      {/* THE CHROME OFFSET LIVES HERE, ONCE.
        *
        * .chrome-nav is position:fixed at top:4px height:68px, so the first
        * 72px of every page sits underneath it. Before this, each hero paid for
        * that itself with an off-ladder padding-top — 144px on nine pages, 128
        * on two, 160 on the 404 — and a hero that used the ladder's 96 instead
        * was left with 24px of visible clearance, which is a collision rather
        * than a rhythm step. /about and /contact shipped exactly that.
        *
        * Clearing fixed chrome was never section rhythm. It is a property of
        * the shell, so it is expressed once at the shell and the section ladder
        * stays honest: every hero now uses the spec's 96 and reads 96 of real
        * clearance.
        *
        * Scoped to this branch deliberately. /login and /admin return earlier
        * and render NO navbar, so an offset on #root would give them 72px of
        * dead space. A wrapper rather than <main> because AboutPage and
        * ContactPage already render their own <main>, and nesting landmarks is
        * invalid. */}
      <div className="chrome-offset">
      <Routes>
        {/* CUT OVER 2026-08-19; landing v1 and v2 deleted 2026-08-31 (see the
            note above the import). The rollback is git history. */}
        <Route path="/" element={<HomeLanding />} />
        <Route path="/ux-audit" element={<UxAuditPillarPage />} />
        <Route path="/ux-auditor" element={<UxAuditorPage />} />
        {/* Review routes for the P1 SEO copy (src/config/seoPreview.js): noindex,
            canonical to the live page, out of the sitemap. Retired at cutover. */}
        {/* A REAL PAGE as of 2026-08-31, not the nginx 301 to
            /ux-auditor/#pricing it replaced. An anchor cannot carry its own
            title, description or canonical, so /pricing could never rank for
            the query it is named after. */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/mvp-validator" element={<MvpValidatorPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/use-cases" element={<UseCasesPage />} />
        <Route path="/use-cases/saas" element={<UseCasesSaasPage />} />
        <Route path="/use-cases/agencies" element={<UseCasesAgenciesPage />} />
        <Route path="/use-cases/startups" element={<UseCasesStartupsPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/audit/new" element={<ProtectedRoute><NewAuditPage /></ProtectedRoute>} />
        <Route path="/audit/:auditId/report" element={<ProtectedRoute><AuditReportPage /></ProtectedRoute>} />
        <Route path="/audit/:auditId/processing" element={<ProtectedRoute><AuditReportPage /></ProtectedRoute>} />
        <Route path="/audits/compare" element={<ProtectedRoute><CompareAuditsPage /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        {/* Public, prerendered, and NOT yet linked or in the sitemap. It is
            noindex until the owner has looked at the rendered page; cutover is
            three reversals, listed at the top of SampleReportPage.jsx. */}
        <Route path="/sample-report" element={<SampleReportPage />} />
        {/* Public, prerendered, noindex, unlinked and out of the sitemap until
            CLAUDE_PAGE_PUBLISHED in claudeContent.js: the /sample-report/ path
            to review, at its final URL rather than a /preview/ route. */}
        <Route path="/claude" element={<ClaudePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/legal" element={<LegalPage />} />
        {/* Any unknown path (including /use-cases/:unknown) renders 404, noindex, no canonical. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </div>
      <Footer />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
